/**
 * R53.20 — IngredientiFotoGallery (Plus only)
 *
 * Galleria foto ingredienti per Produzioni Ristorazione con OCR automatico
 * (Gemini Vision) che estrae nome/marca/lotto/scadenza dall'etichetta.
 *
 * Props:
 *   - foto: array di { storage_path, original_filename, content_type, ocr? }
 *   - dettagli: array di { nome, marca?, lotto?, scadenza?, quantita_confezione?, foto_idx? }
 *   - onFotoChange, onDettagliChange: callback per aggiornare lo state parent
 *   - canUse: boolean → se false, mostra solo banner "feature riservata Plus"
 *   - onUpgrade: callback per aprire UpgradePlusModal se !canUse
 */
import { useState, useRef } from "react";
import api, { formatApiError as apiErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Plus, Trash2, Sparkles, Loader2, Crown, ClipboardList, Wand2, X, Search } from "lucide-react";

export function IngredientiFotoGallery({
  foto = [],
  dettagli = [],
  onFotoChange,
  onDettagliChange,
  canUse = false,
  onUpgrade,
  nomeProduzione = "", // R53.26 — per AI suggest auto-fill
}) {
  const [uploading, setUploading] = useState(false);
  const [ocrIdx, setOcrIdx] = useState(null); // idx foto in OCR
  const fileInputRef = useRef(null);
  // R53.26 — picker ricevimenti + AI suggest
  const [showRicev, setShowRicev] = useState(false);
  const [ricevList, setRicevList] = useState([]);
  const [ricevQ, setRicevQ] = useState("");
  const [loadingRicev, setLoadingRicev] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [aiInput, setAiInput] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSelected, setAiSelected] = useState({}); // {nome: true}
  const [qtyPrompt, setQtyPrompt] = useState(null); // {ricev, qtaUsata} — R53.28 conferma quantità

  // Banner upgrade per piani non-Plus
  if (!canUse) {
    return (
      <button
        type="button"
        onClick={onUpgrade}
        className="w-full rounded-xl border border-dashed border-amber-300 bg-amber-50 px-3 py-3 text-left hover:bg-amber-100 transition"
        data-testid="ingredienti-upgrade-cta"
      >
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-700 shrink-0" />
          <div className="flex-1">
            <p className="text-[12px] font-bold text-amber-900">Galleria foto ingredienti + AI</p>
            <p className="text-[11px] text-amber-700 mt-0.5">
              Scatta foto dei lotti — l&apos;AI legge etichetta, scadenza e lotto automaticamente. Disponibile nel piano Plus.
            </p>
          </div>
        </div>
      </button>
    );
  }

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const newFoto = [...foto];
    const newDettagli = [...dettagli];
    let okCount = 0, ocrCount = 0;
    try {
      for (const f of files) {
        // 1) Upload
        const fd = new FormData();
        fd.append("file", f);
        const { data: up } = await api.post("/preparazioni/upload-photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const photoIdx = newFoto.length;
        newFoto.push({
          storage_path: up.storage_path,
          original_filename: up.original_filename,
          content_type: up.content_type,
          ocr_done: false,
        });
        okCount += 1;
        // 2) R53.29 — OCR IMMEDIATO con lo stesso blob (no round-trip object storage)
        try {
          const fdOcr = new FormData();
          fdOcr.append("file", f);
          const { data: ocr } = await api.post("/preparazioni/extract-ingrediente", fdOcr, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 35000,
          });
          if (ocr && (ocr.nome || ocr.lotto)) {
            newDettagli.push({
              nome: ocr.nome || "",
              marca: ocr.marca || "",
              lotto: ocr.lotto || "",
              scadenza: ocr.scadenza || "",
              quantita_confezione: ocr.quantita_confezione || "",
              unita: ocr.unita || "",
              foto_idx: photoIdx,
              confidence: ocr.confidence,
            });
            newFoto[photoIdx].ocr_done = true;
            ocrCount += 1;
          }
        } catch (ocrErr) {
          console.warn("OCR fallito per foto", f.name, ocrErr);
          // proseguo comunque, foto già caricata
        }
      }
      onFotoChange(newFoto);
      onDettagliChange(newDettagli);
      if (ocrCount > 0) {
        toast.success(`${ocrCount} ingredient${ocrCount === 1 ? "e letto" : "i letti"} dall'AI`);
      } else if (okCount > 0) {
        toast.warning("Foto salvata ma AI non ha letto dati. Compila manualmente o riprova con foto più nitida.");
      }
    } catch (err) {
      toast.error(apiErrMsg(err, "Errore caricamento foto"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleOcr = async (idx, fileObj) => {
    setOcrIdx(idx);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const { data } = await api.post("/preparazioni/extract-ingrediente", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.nome && !data.lotto) {
        toast.warning("AI non ha trovato dati leggibili. Inserisci manualmente.");
        return;
      }
      // Aggiunge una nuova riga ingrediente con foto_idx collegato
      const newDettagli = [
        ...dettagli,
        {
          nome: data.nome || "",
          marca: data.marca || "",
          lotto: data.lotto || "",
          scadenza: data.scadenza || "",
          quantita_confezione: data.quantita_confezione || "",
          unita: data.unita || "",
          foto_idx: idx,
          confidence: data.confidence,
        },
      ];
      onDettagliChange(newDettagli);
      // Segna la foto come "ocr_done"
      const newFoto = foto.map((f, i) => (i === idx ? { ...f, ocr_done: true } : f));
      onFotoChange(newFoto);
      toast.success(`Letto: ${data.nome || "ingrediente"}${data.lotto ? ` · ${data.lotto}` : ""}`);
    } catch (err) {
      toast.error(apiErrMsg(err, "Errore lettura AI"));
    } finally {
      setOcrIdx(null);
    }
  };

  const removeFoto = (idx) => {
    if (!window.confirm("Rimuovere questa foto?")) return;
    const newFoto = foto.filter((_, i) => i !== idx);
    // Rimuove anche le righe ingrediente collegate o le rimappa
    const newDettagli = dettagli
      .filter(d => d.foto_idx !== idx)
      .map(d => ({ ...d, foto_idx: d.foto_idx != null && d.foto_idx > idx ? d.foto_idx - 1 : d.foto_idx }));
    onFotoChange(newFoto);
    onDettagliChange(newDettagli);
  };

  const updateDettaglio = (idx, field, value) => {
    const newDettagli = dettagli.map((d, i) => (i === idx ? { ...d, [field]: value } : d));
    onDettagliChange(newDettagli);
  };

  const addManualRow = () => {
    onDettagliChange([
      ...dettagli,
      { nome: "", marca: "", lotto: "", scadenza: "", quantita_confezione: "", unita: "" },
    ]);
  };

  const removeRow = (idx) => {
    onDettagliChange(dettagli.filter((_, i) => i !== idx));
  };

  // ─── R53.26 — Picker ricevimenti ───────────────────────────────────────────
  const openRicevPicker = async () => {
    setShowRicev(true);
    setLoadingRicev(true);
    try {
      const { data } = await api.get("/produzioni/stock-ricevimenti?limit=100");
      setRicevList(data || []);
    } catch (e) {
      toast.error(apiErrMsg(e, "Impossibile caricare i ricevimenti"));
    } finally { setLoadingRicev(false); }
  };

  const addFromRicevimento = (r, qtaUsata = null) => {
    onDettagliChange([...dettagli, {
      nome: r.prodotto || "",
      marca: r.fornitore || "",
      lotto: r.lotto || "",
      scadenza: r.scadenza || "",
      quantita_confezione: r.quantita != null ? `${r.quantita} ${r.unita || ""}`.trim() : "",
      ricevimento_id: r.id,
      // R53.28: quantità effettivamente usata in questa produzione (opzionale, per tracciabilità + fine lotto)
      quantita_usata: qtaUsata != null && qtaUsata !== "" ? Number(qtaUsata) : null,
      unita_usata: r.unita || "",
    }]);
    toast.success(`${r.prodotto || "Ingrediente"} aggiunto${qtaUsata ? ` · ${qtaUsata} ${r.unita || ""}` : ""}`);
  };

  // ─── R53.26 — AI suggest ingredienti ───────────────────────────────────────
  const openAISuggest = () => {
    setAiInput(nomeProduzione || "");
    setAiResult(null);
    setAiSelected({});
    setShowAI(true);
  };

  const runAISuggest = async () => {
    if (!aiInput || aiInput.length < 2) {
      toast.error("Scrivi il nome della produzione");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/produzioni/suggest-ingredienti", { nome_produzione: aiInput });
      setAiResult(data);
      // Auto-seleziona quelli essenziali
      const pre = {};
      (data.ingredienti || []).forEach(i => { if (i.essenziale) pre[i.nome] = true; });
      setAiSelected(pre);
    } catch (e) {
      toast.error(apiErrMsg(e, "AI non disponibile"));
    } finally { setAiLoading(false); }
  };

  const importAISelected = () => {
    if (!aiResult) return;
    const toAdd = (aiResult.ingredienti || [])
      .filter(i => aiSelected[i.nome])
      .map(i => {
        const m = (i.ricevimenti_match || [])[0];
        return m ? {
          nome: i.nome,
          marca: m.fornitore || "",
          lotto: m.lotto || "",
          scadenza: m.scadenza || "",
          quantita_confezione: m.quantita != null ? `${m.quantita} ${m.unita || ""}`.trim() : "",
          ricevimento_id: m.id,
        } : { nome: i.nome };
      });
    if (toAdd.length === 0) {
      toast.info("Nessun ingrediente selezionato");
      return;
    }
    onDettagliChange([...dettagli, ...toAdd]);
    toast.success(`${toAdd.length} ingredienti aggiunti`);
    setShowAI(false);
  };

  const filteredRicev = ricevList.filter(r => {
    if (!ricevQ) return true;
    const q = ricevQ.toLowerCase();
    return (r.prodotto || "").toLowerCase().includes(q) || (r.lotto || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-3" data-testid="ingredienti-foto-gallery">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold text-slate-700 flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-amber-600" />
          Ingredienti (opzionale — foto + AI)
        </p>
        <span className="text-[10px] text-slate-400 italic">solo Plus</span>
      </div>

      {/* Galleria foto */}
      <div className="grid grid-cols-4 gap-2">
        {foto.map((f, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50 group" data-testid={`ingr-foto-${i}`}>
            <img
              src={`/api/preparazioni/photo-preview?path=${encodeURIComponent(f.storage_path)}`}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <button
              type="button"
              onClick={() => removeFoto(i)}
              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
              data-testid={`ingr-foto-remove-${i}`}
              title="Rimuovi"
            ><Trash2 className="w-3 h-3" /></button>
            {f.ocr_done && (
              <div className="absolute bottom-0 left-0 right-0 bg-emerald-600 text-white text-[9px] text-center py-0.5">AI ✓</div>
            )}
          </div>
        ))}
        {/* Slot upload */}
        <label
          className="aspect-square rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 flex flex-col items-center justify-center cursor-pointer transition"
          data-testid="ingr-foto-add"
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin text-slate-500" /> : <Camera className="w-5 h-5 text-slate-500" />}
          <span className="text-[9px] text-slate-500 mt-1">{uploading ? "Carico..." : "Aggiungi"}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      </div>

      {/* CTA "Leggi con AI" per foto senza OCR */}
      {/* R53.29: rimosso il bottone "ingr-ai-batch" che usava il vecchio
          flusso fetch(photo-preview) con credentials: causava errori silenti
          in mobile Safari/iOS. Adesso OCR avviene automaticamente al momento
          dell'upload (vedi handleUpload). */}

      {/* Tabella ingredienti riconosciuti / aggiunti */}
      {dettagli.length > 0 && (
        <div className="space-y-1.5">
          {dettagli.map((d, i) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-1.5" data-testid={`ingr-row-${i}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={d.nome || ""}
                    onChange={(e) => updateDettaglio(i, "nome", e.target.value)}
                    placeholder="Nome ingrediente"
                    className="w-full text-[12px] font-semibold border-none outline-none p-0"
                  />
                  <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-1">
                    <input
                      type="text"
                      value={d.lotto || ""}
                      onChange={(e) => updateDettaglio(i, "lotto", e.target.value)}
                      placeholder="Lotto"
                      className="text-[10px] font-mono text-slate-600 border-b border-slate-100 outline-none px-1"
                    />
                    <input
                      type="text"
                      value={d.scadenza || ""}
                      onChange={(e) => updateDettaglio(i, "scadenza", e.target.value)}
                      placeholder="Scadenza (YYYY-MM-DD)"
                      className="text-[10px] text-slate-600 border-b border-slate-100 outline-none px-1"
                    />
                    <input
                      type="text"
                      value={d.marca || ""}
                      onChange={(e) => updateDettaglio(i, "marca", e.target.value)}
                      placeholder="Marca"
                      className="text-[10px] text-slate-500 border-b border-slate-100 outline-none px-1"
                    />
                    <input
                      type="text"
                      value={d.quantita_confezione || ""}
                      onChange={(e) => updateDettaglio(i, "quantita_confezione", e.target.value)}
                      placeholder="Qta (es. 500g)"
                      className="text-[10px] text-slate-500 border-b border-slate-100 outline-none px-1"
                    />
                  </div>
                </div>
                {d.foto_idx != null && (
                  <span className="text-[9px] bg-violet-100 text-violet-800 px-1.5 py-0.5 rounded font-semibold shrink-0">📷 #{d.foto_idx + 1}</span>
                )}
                <button
                  type="button"
                  onClick={() => removeRow(i)}
                  className="w-6 h-6 rounded hover:bg-rose-50 text-rose-600 flex items-center justify-center shrink-0"
                  data-testid={`ingr-row-remove-${i}`}
                  title="Rimuovi riga"
                ><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-3 gap-1.5">
        <button
          type="button"
          onClick={addManualRow}
          className="rounded-lg border border-dashed border-slate-300 text-slate-600 text-[10px] font-semibold py-2 flex flex-col items-center justify-center gap-0.5 hover:bg-slate-50"
          data-testid="ingr-add-manual"
        >
          <Plus className="w-3.5 h-3.5" /> Manuale
        </button>
        <button
          type="button"
          onClick={openRicevPicker}
          className="rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px] font-semibold py-2 flex flex-col items-center justify-center gap-0.5 hover:bg-emerald-100"
          data-testid="ingr-add-ricev"
        >
          <ClipboardList className="w-3.5 h-3.5" /> Da Ricevimenti
        </button>
        <button
          type="button"
          onClick={openAISuggest}
          className="rounded-lg border border-violet-300 bg-violet-50 text-violet-800 text-[10px] font-semibold py-2 flex flex-col items-center justify-center gap-0.5 hover:bg-violet-100"
          data-testid="ingr-add-ai"
        >
          <Wand2 className="w-3.5 h-3.5" /> Suggerisci AI
        </button>
      </div>

      {/* R53.26 — Modal Picker Ricevimenti */}
      {showRicev && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-3" onClick={() => setShowRicev(false)} data-testid="ricev-picker-overlay">
          <div className="w-full max-w-md max-h-[80vh] bg-white rounded-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 bg-emerald-700 text-white flex items-center justify-between">
              <p className="font-bold text-sm">Scegli dai Ricevimenti</p>
              <button onClick={() => setShowRicev(false)} className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center" data-testid="ricev-picker-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-2.5 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  value={ricevQ}
                  onChange={(e) => setRicevQ(e.target.value)}
                  placeholder="Cerca prodotto o lotto…"
                  className="w-full pl-8 pr-3 py-2 text-[12px] rounded-lg border border-slate-200 outline-none focus:border-emerald-500"
                  data-testid="ricev-picker-search"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              {loadingRicev && <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>}
              {!loadingRicev && filteredRicev.length === 0 && <p className="text-center py-8 text-[12px] text-slate-400">Nessun ricevimento</p>}
              {filteredRicev.map(r => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setQtyPrompt({ ricev: r, qtaUsata: "" })}
                  className="w-full text-left rounded-lg border border-slate-200 bg-white p-2.5 hover:bg-emerald-50 hover:border-emerald-300 transition"
                  data-testid={`ricev-pick-${r.id}`}
                >
                  <p className="text-[12px] font-bold text-slate-900 truncate">{r.prodotto}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2">
                    <span className="font-mono">{r.lotto || "—"}</span>
                    <span>•</span>
                    <span>{r.scadenza || (r.non_deperibile ? "non deperibile" : "—")}</span>
                    {r.fornitore && <><span>•</span><span className="truncate">{r.fornitore}</span></>}
                  </p>
                  {r.quantita != null && (
                    <p className="text-[10px] text-emerald-700 mt-0.5 font-semibold">📦 {r.quantita} {r.unita || ""} disponibili</p>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* R53.28 — Sotto-modale conferma quantità consumata */}
      {qtyPrompt && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={() => setQtyPrompt(null)} data-testid="qty-prompt-overlay">
          <div className="w-full max-w-sm bg-white rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 bg-emerald-700 text-white">
              <p className="text-[10px] uppercase tracking-widest text-emerald-100 font-bold">Quantità consumata</p>
              <p className="font-bold text-sm truncate mt-0.5">{qtyPrompt.ricev.prodotto}</p>
              <p className="text-[10px] text-emerald-100 mt-0.5">
                Lotto {qtyPrompt.ricev.lotto || "—"} · {qtyPrompt.ricev.quantita || "?"} {qtyPrompt.ricev.unita || ""} in stock
              </p>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-600">Quanto hai usato in questa produzione?</label>
                <p className="text-[10px] text-slate-400 mb-1.5">Facoltativo · serve per tracciare quando il lotto finisce</p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.1"
                    inputMode="decimal"
                    value={qtyPrompt.qtaUsata}
                    onChange={(e) => setQtyPrompt({ ...qtyPrompt, qtaUsata: e.target.value })}
                    placeholder={`Es. ${qtyPrompt.ricev.quantita ? Math.max(0.1, qtyPrompt.ricev.quantita / 4).toFixed(1) : "1.0"}`}
                    className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-emerald-500"
                    data-testid="qty-prompt-input"
                    autoFocus
                  />
                  <span className="text-sm font-semibold text-slate-600 w-8">{qtyPrompt.ricev.unita || ""}</span>
                </div>
              </div>
            </div>
            <div className="p-3 grid grid-cols-2 gap-2 border-t border-slate-200">
              <button
                type="button"
                onClick={() => { addFromRicevimento(qtyPrompt.ricev, null); setQtyPrompt(null); setShowRicev(false); }}
                className="rounded-lg border border-slate-300 py-2 text-[12px] font-semibold"
                data-testid="qty-prompt-skip"
              >Salta</button>
              <button
                type="button"
                onClick={() => { addFromRicevimento(qtyPrompt.ricev, qtyPrompt.qtaUsata); setQtyPrompt(null); setShowRicev(false); }}
                className="rounded-lg bg-emerald-700 text-white py-2 text-[12px] font-bold"
                data-testid="qty-prompt-confirm"
              >Conferma</button>
            </div>
          </div>
        </div>
      )}

      {/* R53.26 — Modal AI Suggest */}
      {showAI && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-3" onClick={() => setShowAI(false)} data-testid="ai-picker-overlay">
          <div className="w-full max-w-md max-h-[85vh] bg-white rounded-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 bg-violet-700 text-white flex items-center justify-between">
              <p className="font-bold text-sm flex items-center gap-1.5"><Wand2 className="w-4 h-4" /> Suggerisci con AI</p>
              <button onClick={() => setShowAI(false)} className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center" data-testid="ai-picker-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-3 space-y-2.5 border-b border-slate-100">
              <p className="text-[11px] text-slate-500">L&apos;AI propone gli ingredienti tipici della preparazione e li abbina ai tuoi ricevimenti.</p>
              <div className="flex gap-2">
                <input
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  placeholder="Es. Tiramisù, Ragù alla bolognese…"
                  className="flex-1 px-3 py-2 text-[12px] rounded-lg border border-slate-200 outline-none focus:border-violet-500"
                  data-testid="ai-picker-input"
                />
                <button
                  type="button"
                  onClick={runAISuggest}
                  disabled={aiLoading}
                  className="px-3 py-2 rounded-lg bg-violet-700 text-white text-[11px] font-bold disabled:opacity-50"
                  data-testid="ai-picker-go"
                >
                  {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Suggerisci"}
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              {aiResult && (aiResult.ingredienti || []).map(i => {
                const matchCount = (i.ricevimenti_match || []).length;
                const isSel = !!aiSelected[i.nome];
                return (
                  <label
                    key={i.nome}
                    className={`flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer transition ${isSel ? "bg-violet-50 border-violet-300" : "bg-white border-slate-200"}`}
                    data-testid={`ai-ingr-${i.nome}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={(e) => setAiSelected({ ...aiSelected, [i.nome]: e.target.checked })}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-[12px] font-bold text-slate-900 truncate">{i.nome}</p>
                        {i.essenziale && <span className="text-[9px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-bold">essenziale</span>}
                      </div>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {matchCount > 0 ? (
                          <span className="text-emerald-700 font-semibold">✓ {matchCount} lotti disponibili</span>
                        ) : (
                          <span className="text-slate-400">nessun lotto in stock</span>
                        )}
                        {" · "}{i.categoria}
                      </p>
                    </div>
                  </label>
                );
              })}
              {aiResult && (aiResult.ingredienti || []).length === 0 && (
                <p className="text-center py-8 text-[12px] text-slate-400">Nessun suggerimento. Riprova con un nome più specifico.</p>
              )}
              {!aiResult && !aiLoading && (
                <p className="text-center py-6 text-[12px] text-slate-400">Scrivi il nome della preparazione e tocca <b>Suggerisci</b>.</p>
              )}
            </div>
            {aiResult && (
              <div className="p-2.5 border-t border-slate-100 grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setShowAI(false)} className="rounded-lg border border-slate-300 py-2 text-[12px] font-semibold" data-testid="ai-picker-cancel">Annulla</button>
                <button type="button" onClick={importAISelected} className="rounded-lg bg-violet-700 text-white py-2 text-[12px] font-bold" data-testid="ai-picker-import">
                  Aggiungi {Object.values(aiSelected).filter(Boolean).length}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
